// src/components/organism/SixDetail/FolderToolbar/FolderToolbar.tsx
import React, { useMemo } from 'react';
import { Button, FolderIcon, ArrowLeftIcon } from 'shared-portal-component';

export interface BreadcrumbSegment {
  label: string;
  path: string;
}

export interface FolderToolbarProps {
  currentPath: string;
  isAtRoot: boolean;
  isUploading: boolean;
  onNavigate: (path: string) => void;
  onNavigateUp: () => void;
  onNewFolder: () => void;
  onUpload: () => void;
}

/**
 * Integrated toolbar for folder navigation and actions.
 * Combines back button, breadcrumb path, and action buttons.
 */
const FolderToolbar: React.FC<FolderToolbarProps> = ({
  currentPath,
  isAtRoot,
  isUploading,
  onNavigate,
  onNavigateUp,
  onNewFolder,
  onUpload,
}) => {
  // Parse path into segments for breadcrumb
  const segments = useMemo<BreadcrumbSegment[]>(() => {
    const result: BreadcrumbSegment[] = [
      { label: 'Files', path: '/' },
    ];

    if (currentPath === '/') {
      return result;
    }

    const parts = currentPath.split('/').filter(Boolean);
    let cumulativePath = '/';

    for (const part of parts) {
      cumulativePath += part + '/';
      result.push({
        label: part,
        path: cumulativePath,
      });
    }

    return result;
  }, [currentPath]);

  return (
    <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg border border-gray-200">
      {/* Left side: Back button + Breadcrumb */}
      <div className="flex items-center gap-3">
        {/* Back button - only show when not at root */}
        {!isAtRoot && (
          <button
            type="button"
            onClick={onNavigateUp}
            className="flex items-center justify-center w-8 h-8 rounded-md hover:bg-gray-200 transition-colors text-gray-600 hover:text-gray-900"
            aria-label="Go back"
          >
            <ArrowLeftIcon size="1x" />
          </button>
        )}

        {/* Breadcrumb path */}
        <nav aria-label="Breadcrumb" className="flex items-center gap-1">
          {segments.map((segment, index) => {
            const isLast = index === segments.length - 1;
            const isFirst = index === 0;

            return (
              <React.Fragment key={segment.path}>
                {index > 0 && (
                  <span className="text-gray-400 mx-1">/</span>
                )}
                {isLast ? (
                  // Current location - not clickable, bold
                  <span className="flex items-center gap-1.5 text-gray-900 font-semibold">
                    {isFirst && <FolderIcon size="1x" />}
                    {segment.label}
                  </span>
                ) : (
                  // Clickable parent
                  <button
                    type="button"
                    onClick={() => { onNavigate(segment.path); }}
                    className="flex items-center gap-1.5 text-gray-600 hover:text-blue-600 hover:underline transition-colors"
                  >
                    {isFirst && <FolderIcon size="1x" />}
                    {segment.label}
                  </button>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      {/* Right side: Action buttons */}
      <div className="flex items-center gap-2">
        <Button
          variant="secondary"
          size="sm"
          onClick={onNewFolder}
          text="New Folder"
        />
        <Button
          variant="primary"
          size="sm"
          onClick={onUpload}
          disabled={isUploading}
          text={isUploading ? 'Uploading...' : 'Upload'}
        />
      </div>
    </div>
  );
};

export default FolderToolbar;
